import {
  getRuntimeConfig,
  isRuntimeConfigLoaded,
} from "@/config/runtimeConfig";
import axios, { AxiosRequestHeaders } from "axios";
import {
  getAccessToken,
  getRefreshToken,
  setLocalAccessToken,
  clearLocalStorage,
} from "@/lib/storeManager";
import { attachJainElibraryGetCaching } from "@/projects/jain elibrary/services/jainElibraryGetCache";

import { logger } from "@/projects/jain elibrary/utils/logger";

function resolveApiBaseUrl(): string {
  if (isRuntimeConfigLoaded()) {
    return getRuntimeConfig().apiUrl;
  }
  return (
    import.meta.env.VITE_JAIN_ELIBRARY_API_URL ?? "http://localhost:8000/api"
  );
}

export const baseURL = resolveApiBaseUrl();

logger.debug("🌐 Jain eLibrary API baseURL:", baseURL);

export const jainElibraryAPI = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];
const onRefreshed = (token: string) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};
const addRefreshSubscriber = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

jainElibraryAPI.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    const isAuthUrl = [
      "/auth/login",
      "/auth/token-refresh",
      "/auth/verify-email",
    ].some(path => originalRequest?.url?.includes(path));

    if (
      error.response?.status === 401 &&
      !isAuthUrl &&
      !originalRequest._retry
    ) {
      if (!getRefreshToken()) {
        return Promise.reject(error);
      }
      if (isRefreshing) {
        return new Promise(resolve => {
          addRefreshSubscriber((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(jainElibraryAPI(originalRequest));
          });
        });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const refresh = getRefreshToken() ?? "";
        const res = await axios.post(
          `${jainElibraryAPI.defaults.baseURL ?? resolveApiBaseUrl()}/auth/token-refresh/`,
          new URLSearchParams({ refresh }),
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        );
        const access = res.data?.access;
        if (access) {
          setLocalAccessToken(access);
          jainElibraryAPI.defaults.headers.common.Authorization = `Bearer ${access}`;
          originalRequest.headers.Authorization = `Bearer ${access}`;
          onRefreshed(access);
          return jainElibraryAPI(originalRequest);
        }
      } catch {
        clearLocalStorage();
        window.location.reload();
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

jainElibraryAPI.interceptors.request.use(
  config => {
    const token = getAccessToken();

    if (!config.headers) {
      config.headers = {} as AxiosRequestHeaders;
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      logger.debug("✅ JWT attached:", config.url);
    } else {
      logger.warnDev("⚠️ No access token:", config.url);
    }
    return config;
  },
  error => {
    logger.error("❌ Request interceptor error:", error);
    return Promise.reject(error);
  }
);

attachJainElibraryGetCaching(jainElibraryAPI);
